import { useCallback, useMemo } from "react";
import { useDataRequest } from "../../common/hooks/api";
import { NetworkRequestState } from "../../common/models";
import { Api } from "../../common/service/Api";
import { PrivacyNoticeDto } from "../../common/service/server/controller/PrivacyNoticeController";
import {
  PrivacyCenterDto,
  PrivacyCenterFontDto,
  PrivacyCenterPolicyDto,
} from "../../common/service/server/dto/PrivacyCenterDto";
import { OrganizationPublicId } from "../../common/service/server/Types";
import {
  CMPSettingsDto,
  CMPStagingSettingsDto,
} from "../../common/service/server/dto/CMPSettingsDto";
import { PrivacyCenterIntegrationsDto } from "../../common/service/server/dto/PrivacyCenterIntegrationsDto";

export const usePrivacyCenter = (
  organizationId: OrganizationPublicId,
  notReady?: boolean,
): [PrivacyCenterDto, NetworkRequestState, () => void] => {
  const api = useCallback(
    () => Api.privacyCenter.getPrivacyCenters(organizationId),
    [organizationId],
  );

  const {
    result: privacyCenters,
    request,
    refresh: fetch,
  } = useDataRequest({
    queryKey: ["privacyCenter", organizationId],
    api,
    notReady,
  });

  const privacyCenter = privacyCenters && privacyCenters.length > 0 && privacyCenters[0];

  return [privacyCenter, request, fetch];
};

export const usePrivacyCenterFonts = (
  orgId: OrganizationPublicId,
  privacyCenterId: string,
): [PrivacyCenterFontDto[] | undefined, NetworkRequestState, () => Promise<void>] => {
  const hasEverything = useMemo(
    () => Boolean(orgId) && Boolean(privacyCenterId),
    [orgId, privacyCenterId],
  );

  const api = useCallback(
    () => Api.privacyCenter.getPrivacyCenterFonts(orgId, privacyCenterId),
    [orgId, privacyCenterId],
  );

  const {
    result: privacyCenterFonts,
    request,
    refresh: fetchFonts,
  } = useDataRequest({
    queryKey: ["privacyCenterFonts", privacyCenterId],
    api,
    notReady: !hasEverything,
  });

  return [privacyCenterFonts, request, fetchFonts];
};

export const usePrivacyPolicy = (
  orgId?: OrganizationPublicId,
  privacyCenterId?: string,
): [PrivacyCenterPolicyDto, NetworkRequestState, () => void] => {
  const hasEverything = useMemo(
    () => Boolean(orgId) && Boolean(privacyCenterId),
    [orgId, privacyCenterId],
  );

  const api = useCallback(
    () => Api.privacyCenter.getPrivacyCenterPolicy(orgId, privacyCenterId),
    [orgId, privacyCenterId],
  );

  const {
    result: privacyPolicy,
    request,
    refresh: fetch,
  } = useDataRequest({
    queryKey: ["privacyPolicy", orgId, privacyCenterId],
    api,
    notReady: !hasEverything,
  });

  return [privacyPolicy, request, fetch];
};

export const usePrivacyNotice = (
  orgId?: OrganizationPublicId,
): [PrivacyNoticeDto, NetworkRequestState, () => void] => {
  const api = useCallback(() => Api.privacyNotice.getCaPrivacyNotice(orgId), [orgId]);
  const {
    result: noticeDto,
    request: noticeRequest,
    refresh: fetch,
  } = useDataRequest({ queryKey: ["privacyNotice", orgId], api, notReady: !orgId });

  return [noticeDto, noticeRequest, fetch];
};

export const useCMPCustomization = (
  orgId,
  privacyCenterId,
  enabled = true,
): [CMPSettingsDto, NetworkRequestState, () => Promise<unknown>] => {
  const hasEverything = useMemo(
    () => Boolean(orgId) && Boolean(privacyCenterId),
    [orgId, privacyCenterId],
  );

  const api = useCallback(
    () => Api.privacyCenter.getPrivacyCenterCMPCustomization(orgId, privacyCenterId),
    [orgId, privacyCenterId],
  );

  const {
    result: cmpCustomization,
    request,
    refresh: getLatestData,
  } = useDataRequest({
    queryKey: ["cmpCustomization", orgId, privacyCenterId],
    api,
    notReady: !hasEverything || enabled === false,
  });

  return [cmpCustomization, request, getLatestData];
};

/** Settings saved for the staging script; `settings` is null until the org first publishes to staging. */
export const useCMPStagingCustomization = (
  orgId,
  privacyCenterId,
): [CMPStagingSettingsDto, NetworkRequestState, () => Promise<unknown>] => {
  const api = useCallback(
    () => Api.privacyCenter.getPrivacyCenterCMPStagingCustomization(orgId, privacyCenterId),
    [orgId, privacyCenterId],
  );

  const {
    result: stagingCustomization,
    request,
    refresh: getLatestData,
  } = useDataRequest({
    queryKey: ["cmpStagingCustomization", orgId, privacyCenterId],
    api,
    notReady: !orgId || !privacyCenterId,
  });

  return [stagingCustomization, request, getLatestData];
};

export const useIntegrations = (
  orgId,
  privacyCenterId,
): [PrivacyCenterIntegrationsDto, NetworkRequestState, () => void] => {
  const hasEverything = useMemo(
    () => Boolean(orgId) && Boolean(privacyCenterId),
    [orgId, privacyCenterId],
  );

  const api = useCallback(
    () => Api.privacyCenter.getPrivacyCenterIntegrations(orgId, privacyCenterId),
    [orgId, privacyCenterId],
  );

  const {
    result: integrations,
    request,
    refresh: getLatestData,
  } = useDataRequest({
    queryKey: ["integrations", orgId, privacyCenterId],
    api,
    notReady: !hasEverything,
  });

  return [integrations, request, getLatestData];
};
